import type { DocsService } from '../../../../modules/doc';
import {
  buildDocsSearchNavigation,
  createDocsSearchNavigationResolvers,
  openDocsSearchNavigation,
} from '../../../../modules/docs-search/services/source-navigation';
import type { Workbench } from '../../../../modules/workbench';
import type {
  AiDocSourceNavigationDetail,
  AiSourceNavigationDetail,
} from './doc-search-source-navigation';

export const AI_SOURCE_MISSING_WARNING = {
  title: 'Source changed',
  message: 'This source is no longer available at its exact location.',
} as const;

const SOURCE_NAVIGATION_DEDUPE_TTL_MS = 1000;
const handledSourceNavigationRequests = new Map<
  string,
  ReturnType<typeof setTimeout>
>();

export interface OpenAiSourceNavigationOptions {
  docsService: DocsService;
  onMissingSource?: (detail: AiDocSourceNavigationDetail) => void;
  workbench: Workbench;
}

export function openAiSourceNavigationDetail(
  detail: AiSourceNavigationDetail | null | undefined,
  options: OpenAiSourceNavigationOptions
): boolean {
  if (!detail) return false;
  if (!claimAiSourceNavigationRequest(detail)) return false;

  switch (detail.kind) {
    case 'mail-source':
      openMailSource(options.workbench, detail);
      return true;
    case 'canvas-affected-ref':
      options.workbench.openDoc({
        docId: detail.docId,
        mode: 'edgeless',
        blockIds: detail.blockIds?.length ? detail.blockIds : undefined,
        elementIds: detail.elementIds?.length ? detail.elementIds : undefined,
        refreshKey: detail.requestId,
      });
      return true;
    case 'doc-source': {
      const navigation = buildDocsSearchNavigation(
        detail.source,
        createDocsSearchNavigationResolvers(options.docsService)
      );
      if (!navigation) {
        options.onMissingSource?.(detail);
        return false;
      }
      openDocsSearchNavigation(options.workbench, navigation, {
        requestId: detail.requestId,
      });
      return true;
    }
  }
}

function claimAiSourceNavigationRequest(
  detail: AiSourceNavigationDetail
): boolean {
  const requestId = detail.requestId;
  if (!requestId) return true;
  if (handledSourceNavigationRequests.has(requestId)) return false;

  const timeout = setTimeout(() => {
    handledSourceNavigationRequests.delete(requestId);
  }, SOURCE_NAVIGATION_DEDUPE_TTL_MS);
  handledSourceNavigationRequests.set(requestId, timeout);
  return true;
}

function openMailSource(
  workbench: Workbench,
  detail: Extract<AiSourceNavigationDetail, { kind: 'mail-source' }>
) {
  const params = new URLSearchParams({
    messageId: detail.messageId,
  });
  if (detail.subject) params.set('subject', detail.subject);
  if (detail.from) params.set('from', detail.from);
  if (detail.receivedAt) params.set('receivedAt', detail.receivedAt);
  if (detail.snippet) params.set('snippet', detail.snippet.slice(0, 240));
  workbench.open(`/mail?${params.toString()}`);
}
